import { useRef, useCallback, useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router';
import useSWR from 'swr';
import { AuthContext } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { usePlatform, useRunTimeEnvironment, useScript } from '../../utils/hooks';
import { runAdmob, useAdsListener } from '../../services/admob';
import {
  getClaimMissionUrl,
  getGetStateUrl,
  getMissionsUrl,
  getMyRankUrl,
  getPrizePoolCurrentUrl,
  getStartAdUrl,
} from '../../api/generated/game-punch/game-punch.ts';
import ArrowLeftImage from '../../assets/icons/arrow_left.png';
import IMG_PIGGY_BANK from '../../assets/icons/punch/pm-piggy-bank.png';
import IMG_CAT_SCORE_ICON from '../../assets/icons/punch/pm-cat-score.png';
import IMG_RANKING_MEDAL_ICON from '../../assets/icons/punch/pm-ranking-medal.png';
import IMG_RANKING_BOARD_STARS from '../../assets/icons/punch/pm-ranking-stars.png';
import IMG_RANKING_BOARD_BADGE from '../../assets/icons/punch/pm-ranking-badge.svg';
import IMG_MISSION_CAT_ICON from '../../assets/icons/punch/pm-mission-cat.png';
import IMG_DONE_ICON from '../../assets/icons/punch/pm-done.svg';
import IMG_LOCK_ICON from '../../assets/icons/punch/pm-lock.svg';
import IMG_CTA_CAT_PARTY from '../../assets/icons/punch/pm-cta-cat-party.png';
import IMG_CTA_VECTOR from '../../assets/icons/punch/pm-cta-vector.svg';
import IMG_CTA_GLOW_YELLOW from '../../assets/icons/punch/pm-cta-glow.svg';
import IMG_WAVE_DIVIDER from '../../assets/icons/punch/pm-wave.svg';
import HighRewardAdIcon from '../../assets/icons/punch/high-reward-ad-icon.svg';
import HighRewardCloseXIcon from '../../assets/icons/punch/high-reward-close-x.svg';
import PunchCoinImage from '../../assets/icons/punch/coin.png';
import PunchWinnerTicker from './PunchWinnerTicker';

// API status → UI state 매핑
// claimed       → 'completed'  (수령 완료 — 완료 버튼)
// claimable     → 'claimable'  (수령 가능 — 받기 버튼 활성)
// in_progress   → 'in_progress' (진행 중 — 자물쇠)
function mapMissionStatus(status) {
  if (status === 'claimed') return 'completed';
  if (status === 'claimable') return 'claimable';
  return 'in_progress';
}

function formatSeasonDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd}`;
}

const CONFETTI_COLORS = [
  '#ff4f8b',
  '#ffcd3c',
  '#4dffb4',
  '#ff7a1a',
  '#c084fc',
  '#60d5ff',
  '#ff6060',
  '#ffffff',
  '#ffa0c8',
  '#6ee7ff',
  '#ffe166',
  '#a78bff',
];

const PRIZE_MISSION_AD_PLACEMENT = Object.freeze({
  CLAIM: 'missionClaim',
  DOUBLE_CHANCE: 'missionDoubleClaim',
});
const MISSION_CLAIM_AD_REQUIRED_LEVEL = 4;

function cRand(a, b) {
  return a + Math.random() * (b - a);
}
function cPick(a) {
  return a[Math.floor(Math.random() * a.length)];
}

function drawStar(c, cx, cy, sp, ou, inn) {
  let r = (Math.PI / 2) * 3;
  const step = Math.PI / sp;
  c.beginPath();
  c.moveTo(cx, cy - ou);
  for (let i = 0; i < sp; i++) {
    c.lineTo(cx + Math.cos(r) * ou, cy + Math.sin(r) * ou);
    r += step;
    c.lineTo(cx + Math.cos(r) * inn, cy + Math.sin(r) * inn);
    r += step;
  }
  c.lineTo(cx, cy - ou);
  c.closePath();
}

class ConfettiParticle {
  constructor(ox, oy) {
    this.x = ox;
    this.y = oy;
    this.color = cPick(CONFETTI_COLORS);
    this.type = ['circle', 'rect', 'star', 'ribbon'][Math.floor(Math.random() * 4)];
    const ang = cRand(-Math.PI * 1.2, -Math.PI * 0.05);
    const spd = cRand(5, 20);
    this.vx = Math.cos(ang) * spd;
    this.vy = Math.sin(ang) * spd;
    this.g = cRand(0.28, 0.52);
    this.drag = cRand(0.968, 0.993);
    this.rot = cRand(0, Math.PI * 2);
    this.rs = cRand(-0.22, 0.22);
    this.sz = cRand(5, 13);
    this.a = 1;
    this.fade = cRand(0.009, 0.018);
    this.rl = cRand(16, 36);
    this.wb = cRand(0, Math.PI * 2);
    this.ws = cRand(0.06, 0.16);
  }
  update() {
    this.vx *= this.drag;
    this.vy = this.vy * this.drag + this.g;
    this.x += this.vx;
    this.y += this.vy;
    this.rot += this.rs;
    this.wb += this.ws;
    this.a -= this.fade;
  }
  draw(c) {
    if (this.a <= 0) return;
    c.save();
    c.globalAlpha = Math.max(0, this.a);
    c.translate(this.x, this.y);
    c.rotate(this.rot);
    c.fillStyle = this.color;
    if (this.type === 'circle') {
      c.beginPath();
      c.arc(0, 0, this.sz * 0.6, 0, Math.PI * 2);
      c.fill();
    } else if (this.type === 'rect') {
      c.fillRect(-this.sz / 2, -this.sz * 0.22, this.sz, this.sz * 0.44);
    } else if (this.type === 'star') {
      drawStar(c, 0, 0, 4, this.sz * 0.75, this.sz * 0.32);
      c.fill();
    } else {
      c.strokeStyle = this.color;
      c.lineWidth = 3.5;
      c.lineCap = 'round';
      c.beginPath();
      for (let i = 0; i <= 5; i++) {
        const t = i / 5;
        const px = (t - 0.5) * this.rl;
        const py = Math.sin(this.wb + t * Math.PI * 2) * 5;
        i === 0 ? c.moveTo(px, py) : c.lineTo(px, py);
      }
      c.stroke();
    }
    c.restore();
  }
}

function useConfetti() {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(null);
  const [rings, setRings] = useState([]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pmBtnPop {
        0%   { transform: scale(1); }
        30%  { transform: scale(0.94); }
        65%  { transform: scale(1.06); }
        100% { transform: scale(1); }
      }
      @keyframes pmRingOut {
        0%   { transform: translate(-50%,-50%) scale(0); opacity: 1; }
        100% { transform: translate(-50%,-50%) scale(4); opacity: 0; }
      }
    `;
    document.head.appendChild(style);

    const canvas = canvasRef.current;
    if (canvas) {
      const resize = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      };
      resize();
      window.addEventListener('resize', resize);
      return () => {
        window.removeEventListener('resize', resize);
        document.head.removeChild(style);
      };
    }
    return () => document.head.removeChild(style);
  }, []);

  const startLoop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || rafRef.current) return;
    const ctx = canvas.getContext('2d');
    const loop = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current.forEach((p) => {
        p.update();
        p.draw(ctx);
      });
      particlesRef.current = particlesRef.current.filter((p) => p.a > 0 && p.y < canvas.height + 80);
      if (particlesRef.current.length > 0) {
        rafRef.current = requestAnimationFrame(loop);
      } else {
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(loop);
  }, []);

  const boom = useCallback(
    (ox, oy) => {
      const ringId = Date.now();
      setRings((prev) => [...prev, { id: ringId, x: ox, y: oy }]);
      setTimeout(() => setRings((prev) => prev.filter((r) => r.id !== ringId)), 600);

      for (let i = 0; i < 140; i++) particlesRef.current.push(new ConfettiParticle(ox, oy));
      [60, 120, 180].forEach((delay) => {
        setTimeout(() => {
          for (let i = 0; i < 50; i++) {
            particlesRef.current.push(new ConfettiParticle(ox + cRand(-25, 25), oy + cRand(-15, 15)));
          }
          startLoop();
        }, delay);
      });
      startLoop();
    },
    [startLoop],
  );

  return { canvasRef, rings, boom };
}

function MissionProgressBar({ state, current, total }) {
  const isClaimed = state === 'completed';
  const fillPercent = isClaimed || state === 'claimable' ? 100 : Math.min(100, (current / total) * 100);
  const label = isClaimed ? '완료' : `${current}/${total}`;
  const fillColor = isClaimed ? '#919191' : '#3b5cd2';
  const bgColor = isClaimed ? '#919191' : '#d9d9d9';

  return (
    <div className="relative h-[20px] w-full rounded-[10px] overflow-hidden" style={{ backgroundColor: bgColor }}>
      <div
        className="absolute left-0 top-0 h-full rounded-[10px]"
        style={{ width: `${fillPercent}%`, backgroundColor: fillColor }}
      />
      <span className="absolute inset-0 flex items-center justify-center font-['Pretendard'] text-[10px] font-bold text-white whitespace-nowrap leading-[1.3]">
        {label}
      </span>
    </div>
  );
}

function MissionContent({ mission }) {
  const isClaimed = mission.state === 'completed';
  return (
    <div className="flex flex-col gap-[8px] h-[72px] items-start w-full">
      <span
        className={`font-['Pretendard'] text-[12px] font-bold leading-[1.2] whitespace-nowrap ${
          isClaimed ? 'text-[#919191]' : 'text-[#3b5cd2]'
        }`}
      >
        {mission.scoreAmount} 냥 스코어
      </span>
      <div className="relative h-[16px] w-[55px] shrink-0">
        <span
          className={`absolute left-0 top-[8px] -translate-y-1/2 font-['Pretendard'] text-[14px] font-bold leading-[1.3] whitespace-nowrap ${
            isClaimed ? 'text-[#919191]' : 'text-[#333]'
          }`}
        >
          펀치 {mission.requirePunches}회
        </span>
      </div>
      <MissionProgressBar state={mission.state} current={mission.currentPunches} total={mission.requirePunches} />
    </div>
  );
}

// claimed — 완료 표시, 비활성 버튼
function CompletedMissionRow({ mission }) {
  return (
    <div className="bg-white flex items-center gap-[12px] p-[16px] rounded-[10px] shadow-[0px_5px_16px_0px_rgba(0,0,0,0.1)] w-full">
      <div className="flex items-center gap-[16px] flex-1 min-w-0">
        <div className="-scale-y-100 rotate-180 shrink-0">
          <img src={IMG_MISSION_CAT_ICON} alt="" className="w-[26px] h-[26px] object-cover grayscale opacity-60" />
        </div>
        <MissionContent mission={mission} />
      </div>
      <button
        type="button"
        aria-label="미션 완료"
        disabled
        className="flex items-center gap-[4px] bg-white border border-[rgba(139,104,221,0.3)] rounded-[8px] p-[10px] shrink-0"
      >
        <span className="font-['Pretendard'] text-[12px] font-bold text-[#919191] leading-[1.3]">완료</span>
        <img src={IMG_DONE_ICON} alt="" className="w-[16px] h-[16px]" />
      </button>
    </div>
  );
}

// in_progress — 자물쇠
function LockedMissionRow({ mission }) {
  return (
    <div className="bg-[#f0ecff] border border-[rgba(139,104,221,0.3)] flex items-center gap-[12px] p-[16px] rounded-[10px] w-full">
      <div className="flex gap-[16px] h-[74px] items-center flex-1 min-w-0">
        <div className="-scale-y-100 rotate-180 shrink-0">
          <img src={IMG_MISSION_CAT_ICON} alt="" className="w-[26px] h-[26px] object-cover" />
        </div>
        <MissionContent mission={mission} />
      </div>
      <button
        type="button"
        aria-label="미션 잠금"
        disabled
        className="flex items-center justify-center h-[46px] px-[15px] py-[10px] rounded-[8px] shrink-0 border-0"
        style={{ backgroundImage: 'linear-gradient(to bottom, #3b5cd2, #8b68dd)' }}
      >
        <img src={IMG_LOCK_ICON} alt="" className="w-[16px] h-[16px]" />
      </button>
    </div>
  );
}

function MissionRow({ mission, onClaim, disabled = false, claimLabel = '스코어 받기' }) {
  if (mission.state === 'completed') return <CompletedMissionRow mission={mission} />;
  if (mission.state === 'claimable') {
    return (
      <ScoreCtaButton
        label={claimLabel}
        variant="purple"
        disabled={disabled}
        onBoom={(x, y) => {
          if (disabled) return;
          onClaim?.(mission.level, { x, y }, mission.scoreAmount);
        }}
      />
    );
  }
  return <LockedMissionRow mission={mission} />;
}

function ScoreCtaButton({ label = '스코어 받기', variant = 'purple', onBoom, disabled = false }) {
  const btnRef = useRef(null);
  const gradient =
    variant === 'blue'
      ? 'linear-gradient(-90deg, #3b5cd2 16%, #8b68dd 85%)'
      : 'linear-gradient(-90deg, #643fe7 16%, #dd50fd 85%)';

  const handleClick = (e) => {
    if (disabled) return;
    const el = btnRef.current;
    if (el) {
      el.style.animation = 'none';
      // eslint-disable-next-line no-unused-expressions
      el.offsetHeight; // reflow to restart animation
      el.style.animation = 'pmBtnPop 0.32s cubic-bezier(.36,.07,.19,.97) both';
    }
    onBoom?.(e.clientX, e.clientY);
  };

  return (
    <button
      ref={btnRef}
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={handleClick}
      className={`flex items-center justify-center rounded-[10px] border-2 border-[#3b5cd2] shadow-[0px_0px_10px_0px_#8b68dd] w-full ${
        disabled ? 'opacity-60 cursor-not-allowed' : ''
      }`}
      style={{ backgroundImage: gradient }}
    >
      <div className="flex items-center justify-center pl-[16px] py-[10px] rounded-[8px]">
        <span className="font-['Pretendard'] text-[18px] font-black text-[#f0ecff] leading-[1.3]">{label}</span>
        <div className="flex items-center justify-center p-[10px] rounded-[100px]">
          <div className="relative w-[60px] h-[60px]">
            <div className="absolute" style={{ width: 50, height: 50, left: 6.5, top: 2 }}>
              <div className="absolute" style={{ inset: '-16px' }}>
                <img src={IMG_CTA_GLOW_YELLOW} alt="" className="block w-full h-full" />
              </div>
            </div>
            <img
              src={IMG_CTA_VECTOR}
              alt=""
              className="absolute block"
              style={{ width: '38.337px', height: '38.079px', left: '3.5px', top: '18.67px' }}
            />
            <img
              src={IMG_CTA_CAT_PARTY}
              alt=""
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />
          </div>
        </div>
      </div>
    </button>
  );
}

function DoubleChancePromptPopup({ scoreAmount, onWatchAd, onSkip, isPrimaryAdLoading = false }) {
  const doubledScore = Math.max(0, Number(scoreAmount) || 0).toLocaleString();
  const primaryLabel = isPrimaryAdLoading ? '로딩 중' : `${doubledScore}스코어 획득하기`;
  return (
    <div
      className="fixed inset-0 z-[10010] flex items-center justify-center bg-black/30 px-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-[320px]">
        <div
          className="absolute left-0 w-[320px] rounded-2xl bg-gradient-to-b from-[#CFCAE8] from-[0%] to-[#EAEAEA] to-[100%] shadow-[0px_5px_16px_0px_rgba(0,0,0,0.1)]"
          style={{ top: '26px', height: '320px' }}
        />
        <button
          type="button"
          aria-label="팝업 닫기"
          onClick={onSkip}
          disabled={isPrimaryAdLoading}
          className="absolute right-5 top-[calc(26px+20px)] z-10 flex h-6 w-6 cursor-pointer items-center justify-center border-0 bg-transparent p-0 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <img src={HighRewardCloseXIcon} alt="" className="h-4 w-4 object-contain" />
        </button>

        <div className="relative flex w-full flex-col items-center gap-[30px] px-[10px] pb-4 pt-0">
          <div className="flex w-[300px] flex-col items-center gap-5">
            <img src={PunchCoinImage} alt="" className="h-20 w-20 object-cover" />
            <h3 className="m-0 text-center font-['Pretendard'] text-[22px] font-extrabold leading-[1.4] text-gray05">
              포인트 2배 찬스
            </h3>
            <p className="m-0 whitespace-pre-line text-center font-['Pretendard'] text-sm font-bold leading-[1.4] text-gray04">
              잠시 광고 보고
              {'\n'}
              냥스코어 2배 획득
            </p>
          </div>

          <div className="flex w-[300px] flex-col items-center gap-[10px]">
            <button
              type="button"
              onClick={onWatchAd}
              disabled={isPrimaryAdLoading}
              className="flex h-[54px] w-[300px] cursor-pointer items-center justify-center gap-[10px] rounded-[8px] border-0 bg-[#1E1E1E] py-4 text-center font-['Pretendard'] text-[16px] font-bold leading-[1.4] text-white disabled:cursor-not-allowed disabled:opacity-55"
            >
              {!isPrimaryAdLoading ? (
                <img src={HighRewardAdIcon} alt="" aria-hidden="true" className="h-4 w-4 object-contain" />
              ) : null}
              <span>{primaryLabel}</span>
            </button>
            <button
              type="button"
              onClick={onSkip}
              disabled={isPrimaryAdLoading}
              className="w-[300px] cursor-pointer rounded-[8px] border-0 bg-[#F4F4F4] py-4 text-center font-['Pretendard'] text-[14px] font-bold leading-[1.4] text-gray03 disabled:cursor-not-allowed disabled:opacity-55"
            >
              무시하고 넘어가기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PunchPrizeMissionPage() {
  const RTE = useRunTimeEnvironment();
  const browserDeviceType = usePlatform();
  useScript(`${process.env.PUBLIC_URL}/flutter/flutter_script.js`);
  const navigate = useNavigate();
  const location = useLocation();
  const { API_SERVER_DOMAIN, useAccessToken, fetchWithToken, appInfo } = useContext(AuthContext);
  const deviceType = appInfo?.os === 'ios' || appInfo?.os === 'android' ? appInfo.os : browserDeviceType;
  const { showToast, showErrorToast } = useToast();
  const accessToken = useAccessToken();
  const { canvasRef, rings, boom } = useConfetti();
  const [isPendingMissionAd, setIsPendingMissionAd] = useState(false);
  const [missionAdLoadingPhase, setMissionAdLoadingPhase] = useState(null);
  const [missionClaimLoadingLevel, setMissionClaimLoadingLevel] = useState(null);
  const [isClaimBusy, setIsClaimBusy] = useState(false);
  const [doubleChancePrompt, setDoubleChancePrompt] = useState(null);
  const claimInFlightRef = useRef(false);
  const pendingBoomPointRef = useRef(null);
  const pendingClaimScoreRef = useRef(0);
  const pendingClaimLevelRef = useRef(null);
  const pendingAdPhaseRef = useRef(null);
  const pendingAdTokenRef = useRef(null);

  useEffect(() => {
    if (typeof window.notifyAds === 'function') return;
    window.notifyAds = (event, reward) => {
      window.dispatchEvent(
        new CustomEvent('ads', {
          detail: { event, reward },
        }),
      );
    };
  }, []);

  const fetchPrizePool = useCallback(
    async (...args) => {
      try {
        return await fetchWithToken(...args);
      } catch (e) {
        console.error('GET /punch/prize-pool/current', e);
        throw e;
      }
    },
    [fetchWithToken],
  );

  const fetchRankingMe = useCallback(
    async (...args) => {
      try {
        return await fetchWithToken(...args);
      } catch (e) {
        console.error('GET /punch/ranking/me', e);
        throw e;
      }
    },
    [fetchWithToken],
  );

  const fetchMissions = useCallback(
    async (...args) => {
      try {
        return await fetchWithToken(...args);
      } catch (e) {
        console.error('GET /punch/missions', e);
        throw e;
      }
    },
    [fetchWithToken],
  );

  const fetchGamePunchState = useCallback(
    async (...args) => {
      try {
        return await fetchWithToken(...args);
      } catch (e) {
        console.error('GET /punch/state', e);
        throw e;
      }
    },
    [fetchWithToken],
  );

  // ── SWR ──────────────────────────────────────────────────────────────────

  const { data: prizePoolData } = useSWR(
    accessToken ? [`${API_SERVER_DOMAIN}${getPrizePoolCurrentUrl()}`, accessToken] : null,
    fetchPrizePool,
    { dedupingInterval: 0, revalidateOnMount: true },
  );

  const { data: rankingMeData, mutate: mutateRankingMe } = useSWR(
    accessToken ? [`${API_SERVER_DOMAIN}${getMyRankUrl()}`, accessToken] : null,
    fetchRankingMe,
    { dedupingInterval: 0, revalidateOnMount: true },
  );

  const { data: missionsData, mutate: mutateMissions } = useSWR(
    accessToken ? [`${API_SERVER_DOMAIN}${getMissionsUrl()}`, accessToken] : null,
    fetchMissions,
    { dedupingInterval: 0, revalidateOnMount: true },
  );

  const { data: gamePunchStateData } = useSWR(
    accessToken ? [`${API_SERVER_DOMAIN}${getGetStateUrl()}`, accessToken] : null,
    fetchGamePunchState,
    { dedupingInterval: 0, revalidateOnMount: true },
  );

  const sendStartAdToBackend = useCallback(
    async (placement) => {
      if (!accessToken || !placement) return false;
      try {
        const response = await fetch(`${API_SERVER_DOMAIN}${getStartAdUrl()}`, {
          method: 'POST',
          cache: 'no-store',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ placement, deviceType }),
        });
        if (!response.ok) {
          const payload = await response.text().catch(() => '');
          console.error('POST /punch/ads/start failed', { placement, status: response.status, payload });
          showErrorToast('광고를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.');
          return false;
        }
        const data = await response.json().catch(() => null);
        return {
          adToken: data?.adToken ?? null,
          placementId: data?.placementId ?? '',
        };
      } catch (error) {
        console.error('POST /punch/ads/start error', { placement, error });
        showErrorToast('광고를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.');
        return false;
      }
    },
    [API_SERVER_DOMAIN, accessToken, deviceType, showErrorToast],
  );

  const postMissionClaim = useCallback(
    async (level, { adToken, doubleChanceFlag } = {}) => {
      if (!accessToken) return null;
      try {
        const claimBody = {
          level,
          doubleChanceFlag:
            typeof doubleChanceFlag === 'string' && doubleChanceFlag.length > 0 ? doubleChanceFlag : 'false',
          ...(typeof adToken === 'string' && adToken.length > 0 ? { adToken } : {}),
        };
        const response = await fetch(`${API_SERVER_DOMAIN}${getClaimMissionUrl()}`, {
          method: 'POST',
          cache: 'no-store',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(claimBody),
        });
        if (!response.ok) {
          const payload = await response.text().catch(() => '');
          console.error('POST /punch/missions/claim failed', { level, claimBody, status: response.status, payload });
          showErrorToast('미션 클레임에 실패했어요. 잠시 후 다시 시도해 주세요.');
          return null;
        }
        const data = await response.json().catch(() => null);
        if (data?.result && data.result !== 'ok') {
          const message =
            typeof data?.message === 'string' && data.message.trim().length > 0
              ? data.message
              : '미션 클레임에 실패했어요. 잠시 후 다시 시도해 주세요.';
          console.error('POST /punch/missions/claim fail result', { level, data });
          showErrorToast(message);
          return null;
        }
        if (typeof data?.nyangScore === 'number') {
          mutateRankingMe(
            (prev) => ({
              ...(prev ?? {}),
              myNyangScore: data.nyangScore,
            }),
            false,
          );
        } else {
          mutateRankingMe();
        }
        if (typeof data?.score === 'number' && data.score > 0) {
          showToast(`${data.score.toLocaleString()} 냥스코어 획득!`);
        }
        mutateMissions();
        return data;
      } catch (error) {
        console.error('POST /punch/missions/claim error', { level, error });
        showErrorToast('미션 클레임에 실패했어요. 잠시 후 다시 시도해 주세요.');
        return null;
      }
    },
    [API_SERVER_DOMAIN, accessToken, mutateMissions, mutateRankingMe, showErrorToast, showToast],
  );

  const clearMissionAdLoading = useCallback(() => {
    setMissionAdLoadingPhase(null);
    setMissionClaimLoadingLevel(null);
  }, []);

  const runMissionAd = useCallback(
    (phase, level, adStart) => {
      if (typeof window.flutter_inappwebview?.callHandler !== 'function') {
        claimInFlightRef.current = false;
        setIsClaimBusy(false);
        clearMissionAdLoading();
        showErrorToast('광고 재생에 실패했어요.');
        return false;
      }
      pendingAdPhaseRef.current = phase;
      pendingClaimLevelRef.current = level;
      pendingAdTokenRef.current = adStart?.adToken ?? null;
      setIsPendingMissionAd(true);
      runAdmob('', adStart?.placementId ?? '');
      return true;
    },
    [clearMissionAdLoading, showErrorToast],
  );

  const triggerPendingClaimBoom = useCallback(() => {
    const point = pendingBoomPointRef.current;
    pendingBoomPointRef.current = null;

    if (typeof point?.x === 'number' && typeof point?.y === 'number') {
      boom?.(point.x, point.y);
      return;
    }

    boom?.(window.innerWidth / 2, window.innerHeight / 2);
  }, [boom]);

  const handleMissionAdError = useCallback(
    (errorInfo = {}) => {
      claimInFlightRef.current = false;
      setIsClaimBusy(false);
      pendingBoomPointRef.current = null;
      pendingClaimScoreRef.current = 0;
      pendingAdPhaseRef.current = null;
      pendingClaimLevelRef.current = null;
      pendingAdTokenRef.current = null;
      setIsPendingMissionAd(false);
      clearMissionAdLoading();
      const message =
        typeof errorInfo?.message === 'string' && errorInfo.message.trim().length > 0
          ? errorInfo.message
          : '광고 재생에 실패했어요. 잠시 후 다시 시도해 주세요.';
      showErrorToast(message);
    },
    [clearMissionAdLoading, showErrorToast],
  );

  useAdsListener(
    '',
    '',
    {
      task: 'prize-mission-ad',
      pending: isPendingMissionAd,
      silentErrorAlert: true,
      onError: handleMissionAdError,
    },
    async () => {
      if (!isPendingMissionAd) return;

      const phase = pendingAdPhaseRef.current;
      const level = pendingClaimLevelRef.current;
      const adToken = pendingAdTokenRef.current;
      pendingAdPhaseRef.current = null;
      pendingClaimLevelRef.current = null;
      pendingAdTokenRef.current = null;
      setIsPendingMissionAd(false);
      clearMissionAdLoading();

      if (phase === 'claim' && typeof level === 'number') {
        const claimResult = await postMissionClaim(level, { adToken, doubleChanceFlag: 'false' });
        if (claimResult) {
          triggerPendingClaimBoom();
        }
        if (!claimResult?.doubleChance || RTE !== 'APP') {
          pendingClaimScoreRef.current = 0;
          claimInFlightRef.current = false;
          setIsClaimBusy(false);
          return;
        }
        setDoubleChancePrompt({
          level,
          doubledScore: Math.max(0, (pendingClaimScoreRef.current || claimResult?.score || 0) * 2),
        });
        claimInFlightRef.current = false;
        setIsClaimBusy(false);
        return;
      }

      if (phase === 'doubleChance' && typeof level === 'number') {
        await postMissionClaim(level, { adToken, doubleChanceFlag: 'true' });
        setDoubleChancePrompt(null);
        pendingClaimScoreRef.current = 0;
      }

      claimInFlightRef.current = false;
      setIsClaimBusy(false);
    },
  );

  const handleClaim = useCallback(
    async (level, boomPoint, missionScore) => {
      if (!accessToken || claimInFlightRef.current || missionAdLoadingPhase) return;
      claimInFlightRef.current = true;
      setIsClaimBusy(true);
      pendingBoomPointRef.current = boomPoint ?? null;
      pendingClaimScoreRef.current = Number(missionScore) || 0;

      const normalizedLevel = Number(level);
      const shouldSkipClaimAd = Number.isFinite(normalizedLevel) && normalizedLevel < MISSION_CLAIM_AD_REQUIRED_LEVEL;

      if (RTE !== 'APP' || shouldSkipClaimAd) {
        const claimResult = await postMissionClaim(level, { doubleChanceFlag: 'false' });
        if (claimResult) {
          triggerPendingClaimBoom();
        } else {
          pendingBoomPointRef.current = null;
        }
        if (claimResult?.doubleChance && RTE === 'APP') {
          setDoubleChancePrompt({
            level,
            doubledScore: Math.max(0, (pendingClaimScoreRef.current || claimResult?.score || 0) * 2),
          });
        } else {
          pendingClaimScoreRef.current = 0;
        }
        claimInFlightRef.current = false;
        setIsClaimBusy(false);
        return;
      }

      setMissionAdLoadingPhase('claim');
      setMissionClaimLoadingLevel(level);
      try {
        const claimAdToken = await sendStartAdToBackend(PRIZE_MISSION_AD_PLACEMENT.CLAIM);
        if (claimAdToken === false) {
          claimInFlightRef.current = false;
          setIsClaimBusy(false);
          pendingBoomPointRef.current = null;
          pendingClaimScoreRef.current = 0;
          clearMissionAdLoading();
          return;
        }

        const adStarted = runMissionAd('claim', level, claimAdToken);
        if (!adStarted) {
          pendingBoomPointRef.current = null;
          pendingClaimScoreRef.current = 0;
          clearMissionAdLoading();
        }
      } catch (error) {
        claimInFlightRef.current = false;
        setIsClaimBusy(false);
        pendingBoomPointRef.current = null;
        pendingClaimScoreRef.current = 0;
        clearMissionAdLoading();
        throw error;
      }
    },
    [
      RTE,
      accessToken,
      clearMissionAdLoading,
      missionAdLoadingPhase,
      postMissionClaim,
      runMissionAd,
      sendStartAdToBackend,
      triggerPendingClaimBoom,
    ],
  );

  const handleWatchDoubleChanceAd = useCallback(async () => {
    if (!doubleChancePrompt?.level || missionAdLoadingPhase) return;
    claimInFlightRef.current = true;
    setIsClaimBusy(true);
    const targetLevel = doubleChancePrompt.level;

    setMissionAdLoadingPhase('doubleChance');
    try {
      const doubleChanceToken = await sendStartAdToBackend(PRIZE_MISSION_AD_PLACEMENT.DOUBLE_CHANCE);
      if (doubleChanceToken === false) {
        claimInFlightRef.current = false;
        setIsClaimBusy(false);
        pendingClaimScoreRef.current = 0;
        clearMissionAdLoading();
        return;
      }

      const started = runMissionAd('doubleChance', targetLevel, doubleChanceToken);
      if (!started) {
        claimInFlightRef.current = false;
        setIsClaimBusy(false);
        pendingClaimScoreRef.current = 0;
        clearMissionAdLoading();
      }
    } catch (error) {
      claimInFlightRef.current = false;
      setIsClaimBusy(false);
      pendingClaimScoreRef.current = 0;
      clearMissionAdLoading();
      throw error;
    }
  }, [clearMissionAdLoading, doubleChancePrompt, missionAdLoadingPhase, runMissionAd, sendStartAdToBackend]);

  const handleSkipDoubleChanceAd = useCallback(() => {
    if (missionAdLoadingPhase) return;
    setDoubleChancePrompt(null);
    pendingClaimScoreRef.current = 0;
    claimInFlightRef.current = false;
    setIsClaimBusy(false);
  }, [missionAdLoadingPhase]);

  const isMissionClaimDisabled = isClaimBusy || !!doubleChancePrompt || !!missionAdLoadingPhase;
  const routeSeasonMeta = location.state ?? {};

  const data = {
    prizeAmount: prizePoolData?.poolAmount ?? 0,
    seasonNo: gamePunchStateData?.seasonNo ?? routeSeasonMeta.seasonNo ?? prizePoolData?.seasonNo ?? null,
    seasonStartDate: formatSeasonDate(gamePunchStateData?.seasonStartDate ?? routeSeasonMeta.seasonStartDate),
    seasonEndDate: formatSeasonDate(gamePunchStateData?.seasonEndDate ?? routeSeasonMeta.seasonEndDate),
    myNyangScore: rankingMeData?.myNyangScore ?? 0,
    myRanking: rankingMeData?.myRanking ?? '-',
    cumulativePunchCount: missionsData?.missionPunchCount ?? 0,
    missions: (missionsData?.missions ?? []).map((m) => ({
      id: m.level,
      level: m.level,
      scoreAmount: m.score ?? 0,
      requirePunches: m.requiredPunchCount ?? 0,
      currentPunches: m.progressPunchCount ?? 0,
      state: mapMissionStatus(m.status),
    })),
  };
  const prizeRoundPeriodLabel =
    data.seasonNo && data.seasonStartDate && data.seasonEndDate
      ? `${data.seasonNo}회차 ${data.seasonStartDate} ~ ${data.seasonEndDate}`
      : '';

  const completedMissions = data.missions.filter((m) => m.state === 'completed');
  const pendingMissions = data.missions.filter((m) => m.state !== 'completed');

  return (
    <section className="relative min-h-screen overflow-x-hidden bg-white">
      {/* Confetti canvas — fixed over viewport */}
      <canvas ref={canvasRef} className="fixed inset-0 w-full h-full pointer-events-none" style={{ zIndex: 9999 }} />
      {/* Flash rings */}
      {rings.map((ring) => (
        <div
          key={ring.id}
          className="fixed rounded-full pointer-events-none"
          style={{
            left: ring.x,
            top: ring.y,
            width: 50,
            height: 50,
            background: 'rgba(255,220,80,0.65)',
            animation: 'pmRingOut 0.55s ease-out forwards',
            zIndex: 9998,
          }}
        />
      ))}

      <div className="bg-[#f0ecff]">
        <header className="flex h-[46px] items-center px-[10px]" aria-label="상단 네비게이션">
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0"
            onClick={() => navigate(-1)}
            aria-label="뒤로가기"
          >
            <img src={ArrowLeftImage} alt="" className="h-6 w-6 object-contain" />
          </button>
        </header>

        <PunchWinnerTicker
          accessToken={accessToken}
          API_SERVER_DOMAIN={API_SERVER_DOMAIN}
          fetchWithToken={fetchWithToken}
        />

        <div className="relative z-[1] px-[10px] pt-[20px] pb-0">
          <div className="flex items-center justify-between pl-[10px] mb-[10px]">
            <div className="flex flex-col gap-[4px]">
              <h1 className="m-0 font-['Pretendard'] text-[24px] font-bold text-[#333] leading-[1.3]">펀치상금통</h1>
              <p className="m-0 font-['Pretendard'] text-[13px] font-bold leading-[1.3] text-[#919191]">
                스코어를 받고 랭킹을 올려 상금을 받으세요!
              </p>
            </div>
            <img src={IMG_PIGGY_BANK} alt="상금통 돼지저금통" className="w-[80px] h-[80px] object-cover" />
          </div>

          <div className="bg-white rounded-[8px] px-[20px] py-[10px] mb-[8px]">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="font-['Pretendard'] text-[14px] font-bold text-[#8B68DD] leading-[1.3]">
                  이번 회차 총 상금
                </span>
                {prizeRoundPeriodLabel ? (
                  <p className="m-0 mt-[5px] font-['Pretendard'] text-[14px] font-medium leading-[1.3] text-[#919191]">
                    <span className="text-[#8b68dd]">{`${data.seasonNo}회차`}</span>{' '}
                    {`${data.seasonStartDate} ~ ${data.seasonEndDate}`}
                  </p>
                ) : null}
              </div>
              <span className="self-center font-['Pretendard'] text-[24px] font-bold text-[#8b68dd] leading-[1.3]">
                {(data.prizeAmount ?? 0).toLocaleString()} P
              </span>
            </div>
          </div>

          <div className="flex items-center gap-[8px]">
            <div
              className="flex-1 aspect-[6/5] bg-[rgba(255,255,255,0.6)] rounded-[8px] shadow-[0px_5px_16px_0px_rgba(0,0,0,0.1)] flex items-center justify-center"
              aria-label="내 냥 스코어"
            >
              <div className="flex flex-col gap-[12px] w-[74%]">
                <span className="font-['Pretendard'] text-[12px] font-bold text-[#919191] leading-[1.3]">
                  내 냥 스코어
                </span>
                <div className="flex items-center justify-between">
                  <img src={IMG_CAT_SCORE_ICON} alt="" className="w-[34px] h-[34px] object-cover shrink-0" />
                  <span className="font-['Pretendard'] text-[24px] font-bold text-[#333] leading-[1.3]">
                    {data.myNyangScore ?? 0}
                  </span>
                </div>
              </div>
            </div>

            <div
              className="flex-1 aspect-[6/5] bg-[rgba(255,255,255,0.6)] rounded-[8px] shadow-[0px_5px_16px_0px_rgba(0,0,0,0.1)] flex items-center justify-center"
              aria-label="내 랭킹"
            >
              <div className="flex flex-col gap-[12px] w-[74%]">
                <span className="font-['Pretendard'] text-[12px] font-bold text-[#919191] leading-[1.3]">내 랭킹</span>
                <div className="flex items-center justify-between">
                  <img src={IMG_RANKING_MEDAL_ICON} alt="" className="w-[34px] h-[34px] object-cover shrink-0" />
                  <span className="font-['Pretendard'] text-[24px] font-bold text-[#333] leading-[1.3]">
                    {data.myRanking ?? '-'}
                  </span>
                </div>
              </div>
            </div>

            <div
              className="relative flex-1 aspect-[6/5] rounded-[10px] border-[3px] border-[rgba(139,104,221,0.3)] shadow-[0px_5px_16px_0px_rgba(0,0,0,0.1)] overflow-visible cursor-pointer flex flex-col items-center justify-center gap-[4px]"
              style={{ backgroundImage: 'linear-gradient(-41.75deg, #3b5cd2 16.832%, #8b68dd 76.295%)' }}
              role="button"
              tabIndex={0}
              aria-label="랭킹보드 보기"
              onClick={() => navigate('/punch/ranking')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') navigate('/punch/ranking');
              }}
            >
              <img src={IMG_RANKING_BOARD_STARS} alt="" className="w-[49%] object-contain pointer-events-none" />
              <span className="font-['Pretendard'] text-[12px] font-bold text-[#ffbc2b] leading-[1.3] whitespace-nowrap">
                랭킹보드
              </span>
              <div className="absolute" style={{ right: '-4px', top: '-6px', width: '16px', height: '16px' }}>
                <div className="absolute" style={{ inset: '-4px' }}>
                  <img src={IMG_RANKING_BOARD_BADGE} alt="" className="block w-full h-full" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative h-[60px] overflow-hidden bg-[#f0ecff]" aria-hidden="true">
        <div className="absolute right-0 bottom-0 left-0" style={{ top: '30%' }}>
          <img src={IMG_WAVE_DIVIDER} alt="" className="absolute block inset-0 w-full h-full" />
        </div>
      </div>

      <section className="flex flex-col gap-[10px] px-[10px] pt-[10px] pb-[40px]" aria-label="상금통 미션 목록">
        <div className="bg-white border border-[#f4f4f4] rounded-[10px] shadow-[-6px_5px_16px_0px_rgba(0,0,0,0.1)] h-[60px] flex items-center justify-between px-[20px]">
          <div className="flex items-center gap-[8px]">
            <div className="-scale-y-100 rotate-180">
              <img src={IMG_MISSION_CAT_ICON} alt="" className="w-[26px] h-[26px] object-cover" />
            </div>
            <span className="font-['Pretendard'] text-[14px] font-bold text-[#5e5e5e] leading-[1.3]">누적 펀치수</span>
          </div>
          <span className="font-['Pretendard'] text-[20px] font-bold text-[#5e5e5e] leading-[1.3]">
            {data.cumulativePunchCount ?? 0}
          </span>
        </div>

        {pendingMissions.map((m) => (
          <MissionRow
            key={m.id}
            mission={m}
            onClaim={handleClaim}
            disabled={isMissionClaimDisabled}
            claimLabel={missionClaimLoadingLevel === m.level ? '로딩 중' : '스코어 받기'}
          />
        ))}

        {completedMissions.map((m) => (
          <MissionRow
            key={m.id}
            mission={m}
            onClaim={handleClaim}
            disabled={isMissionClaimDisabled}
            claimLabel={missionClaimLoadingLevel === m.level ? '로딩 중' : '스코어 받기'}
          />
        ))}
      </section>

      {doubleChancePrompt ? (
        <DoubleChancePromptPopup
          scoreAmount={doubleChancePrompt.doubledScore}
          isPrimaryAdLoading={missionAdLoadingPhase === 'doubleChance'}
          onWatchAd={handleWatchDoubleChanceAd}
          onSkip={handleSkipDoubleChanceAd}
        />
      ) : null}
    </section>
  );
}
